import { ToastVariant } from "../../stores/toasts";

import { cn } from "@networx/utils/tailwind/cn";

import { LegacyToast } from "./Legacy";

import CheckIcon from "./assets-jsx/CheckIcon";
import CloseIcon from "./assets-jsx/CloseIcon"
import ErrorIcon from "./assets-jsx/ErrorIcon";
import WarnIcon from "./assets-jsx/WarnIcon";
/**
 * If we don't get an `onClose` we don't show the Close Icon.
 *
 * If the text contains a long title or a description we align the icons to the top (not center)
 */
export default function Toast({ variant, title, description, htmlContent, onClose }: Props) {
  // set the defaults
  variant = variant || "no-icon";

  const withDescription = !!description;
  const withHtmlContent = !!htmlContent;
  const withLongTitle = title.length > 30;
  const alignStart = withDescription || withLongTitle;
  const variantClass = cn("inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg", {
    "text-success-500 bg-success-100": variant === "success",
    "text-danger-500 bg-danger-100": variant === "error",
    "text-warning-500 bg-warning-100": variant === "warn",
  });

  // This is a special variant for the success toast
  // it is here to be compatible with the old style of toasts
  if (variant === "success-solid") {
    return <LegacyToast title={title} onClose={onClose} color="success" />;
  }

  return (
    <div
      className={cn("bg-content1 text-default-700 flex w-full rounded-lg p-4 shadow dark:shadow-none", {
        "items-center": !alignStart,
        "items-start": alignStart,
      })}
      role="alert"
    >
      {variant !== "no-icon" && (
        <div className={variantClass}>
          {variant === "success" && <CheckIcon className="h-5 w-5" />}
          {variant === "warn" && <WarnIcon className="h-5 w-5" />}
          {variant === "error" && <ErrorIcon className="h-5 w-5" />}
          <span className="sr-only">Toast icon</span>
        </div>
      )}
      <div className="flex flex-col justify-start">
        <div className="ms-3 text-sm font-bold">{title}</div>
        {withHtmlContent ?
          (<>
            {htmlContent}
          </>) : (
            withDescription ? (<div className="text-default-500 ms-3 text-sm font-normal">{description}</div>) : null
          )}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="bg-content1 text-default-400 hover:bg-content2 hover:text-default-900 focus:ring-default-300 -mx-1.5 -my-1.5 ms-auto inline-flex h-8 w-8 items-center justify-center rounded-lg p-1.5 focus:ring-2"
          aria-label="Close"
        >
          <span className="sr-only">Close</span>
          <CloseIcon className="stroke-default-400 h-3 w-3" />
        </button>
      )}
    </div>
  );
}

interface Props {
  variant?: ToastVariant;
  title: string;
  description?: string;
  htmlContent?: React.ReactNode;
  onClose?: () => void;
}
